import { useState } from 'react';
import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { ErrorDisplay } from '@/components/error';
import { createAppError, type AppError } from '@/types/error.types';
import { 
  AlertTriangle, 
  Bug, 
  Network, 
  ShieldAlert, 
  Database, 
  Server,
  CheckCircle2,
  XCircle
} from 'lucide-react';

export function ErrorHandlingDemoPage() {
  const { handleError, handleApiError, handleValidationError, clearError } = useErrorHandler();
  const [demoError, setDemoError] = useState<AppError | null>(null);
  const [lastAction, setLastAction] = useState<string>('');

  const showSuccess = (action: string) => {
    setLastAction(action);
    setTimeout(() => setLastAction(''), 3000);
  };

  // Demo error scenarios
  const triggerNetworkError = () => {
    handleApiError(
      new Error('Failed to fetch data'),
      '/api/projects',
      'GET'
    );
    showSuccess('Network error triggered');
  };

  const triggerValidationError = () => {
    handleValidationError(
      'Please correct the following validation errors',
      {
        projectName: ['Project name is required', 'Must be at least 3 characters'],
        budget: ['Budget must be greater than 0'],
        startDate: ['Start date cannot be in the past'],
      }
    );
    showSuccess('Validation error triggered');
  };

  const triggerAuthError = () => {
    handleError(
      new Error('Session expired'),
      {
        message: 'Your session has expired. Please log in again.',
        category: 'authentication',
        severity: 'error',
        suggestedActions: [
          'Click the login button to sign in again',
          'Clear your browser cache if the issue persists',
        ],
      },
      {
        canRetry: false,
        customAction: {
          label: 'Go to Login',
          onClick: () => alert('Would redirect to login page'),
        },
      }
    );
    showSuccess('Authentication error triggered');
  };

  const triggerCriticalError = () => {
    handleError(
      new Error('Database connection failed'),
      {
        message: 'A critical system error has occurred',
        category: 'system',
        severity: 'critical',
        technicalDetails: {
          errorCode: 'DB_CONNECTION_FAILED',
          timestamp: new Date().toISOString(),
          userAgent: window.navigator.userAgent,
          url: window.location.href,
        },
        suggestedActions: [
          'The system is experiencing technical difficulties',
          'Our team has been notified',
          'Please try again in a few minutes',
        ],
        recoverable: false,
      }
    );
    showSuccess('Critical error triggered');
  };

  const showInlineError = () => {
    const error = createAppError(
      new Error('Failed to load project data'),
      {
        message: 'Unable to load project details',
        category: 'data',
        severity: 'warning',
        suggestedActions: [
          'Check your internet connection',
          'Try refreshing the page',
        ],
      }
    );
    error.recovery = {
      canRetry: true,
      onRetry: () => {
        setDemoError(null);
        showSuccess('Retry clicked');
      },
    };
    setDemoError(error);
    showSuccess('Inline error displayed');
  };

  const showCompactError = () => {
    const error = createAppError(
      new Error('Minor issue'),
      {
        message: 'This is a compact error notification',
        category: 'data',
        severity: 'info',
      }
    );
    setDemoError(error);
    showSuccess('Compact error displayed');
  };

  const throwReactError = () => {
    // This will be caught by ErrorBoundary
    throw new Error('Simulated React component error');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Error Handling System"
        description="Comprehensive error handling with user-friendly UX and technical details"
      />

      {/* Status Banner */}
      {lastAction && (
        <Card className="border-success-200 bg-success-50 dark:border-success-900 dark:bg-success-950">
          <CardContent className="flex items-center gap-2 py-3">
            <CheckCircle2 className="h-5 w-5 text-success-600 dark:text-success-400" />
            <span className="text-sm font-medium text-success-900 dark:text-success-100">
              {lastAction}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Features Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Key Features</CardTitle>
          <CardDescription>
            World-class error handling designed for mid-market ERP applications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-primary/10 p-2">
                  <AlertTriangle className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">User-Friendly Messages</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Clear, actionable error messages tailored for business users
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-primary/10 p-2">
                  <Bug className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">Technical Details</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Complete error context with one-click copy for support tickets
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-primary/10 p-2">
                  <Network className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">Smart Recovery</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Automatic retry, reload, and custom recovery actions
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-primary/10 p-2">
                  <ShieldAlert className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">Error Categorization</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Network, validation, auth, data, and system errors
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-primary/10 p-2">
                  <Database className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">Error Boundary</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Catches React errors and prevents app crashes
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-primary/10 p-2">
                  <Server className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">Multiple Display Modes</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Full-page, inline, compact, and toast notifications
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Error Scenarios */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Context-Based Errors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Context-Based Errors
            </CardTitle>
            <CardDescription>
              Errors shown in the global error context (full-page display)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={triggerNetworkError}
            >
              <Network className="mr-2 h-4 w-4" />
              Network Error
              <Badge variant="secondary" className="ml-auto">
                Error
              </Badge>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={triggerValidationError}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Validation Error
              <Badge variant="secondary" className="ml-auto">
                Warning
              </Badge>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={triggerAuthError}
            >
              <ShieldAlert className="mr-2 h-4 w-4" />
              Authentication Error
              <Badge variant="secondary" className="ml-auto">
                Error
              </Badge>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={triggerCriticalError}
            >
              <Server className="mr-2 h-4 w-4" />
              Critical System Error
              <Badge variant="destructive" className="ml-auto">
                Critical
              </Badge>
            </Button>

            <Separator />

            <Button
              variant="destructive"
              className="w-full"
              onClick={throwReactError}
            >
              <Bug className="mr-2 h-4 w-4" />
              Throw React Error (Error Boundary)
            </Button>
          </CardContent>
        </Card>

        {/* Inline Errors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Inline Error Display
            </CardTitle>
            <CardDescription>
              Errors shown inline within components
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={showInlineError}
            >
              Show Inline Error
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={showCompactError}
            >
              Show Compact Error
            </Button>

            {demoError && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Preview:</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDemoError(null)}
                    >
                      Clear
                    </Button>
                  </div>
                  <ErrorDisplay
                    error={demoError}
                    variant={demoError.severity === 'info' ? 'compact' : 'inline'}
                    recovery={demoError.recovery}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Implementation Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Implementation Guide</CardTitle>
          <CardDescription>
            How to use the error handling system in your components
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold">1. Using the Error Hook</h3>
            <pre className="rounded-md bg-muted p-4 text-xs">
{`import { useErrorHandler } from '@/hooks/use-error-handler';

function MyComponent() {
  const { handleError, handleApiError } = useErrorHandler();
  
  const fetchData = async () => {
    try {
      const response = await fetch('/api/data');
      if (!response.ok) throw new Error('Failed to fetch');
    } catch (error) {
      handleApiError(error, '/api/data', 'GET');
    }
  };
}`}
            </pre>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">2. Using Error Boundary</h3>
            <pre className="rounded-md bg-muted p-4 text-xs">
{`import { ErrorBoundary } from '@/components/error';

<ErrorBoundary>
  <MyComponent />
</ErrorBoundary>`}
            </pre>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">3. Inline Error Display</h3>
            <pre className="rounded-md bg-muted p-4 text-xs">
{`import { ErrorDisplay } from '@/components/error';

{error && (
  <ErrorDisplay
    error={error}
    variant="inline"
    recovery={{
      canRetry: true,
      onRetry: handleRetry,
    }}
  />
)}`}
            </pre>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">4. Copy to Clipboard Feature</h3>
            <p className="text-sm text-muted-foreground">
              All error displays include a "Copy Error Details" button that formats the complete
              error information (including stack traces, timestamps, and technical details) ready
              for pasting into support tickets or bug reports.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
